import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, Bell, Calendar, CheckCircle, AlertTriangle } from "lucide-react";

var API_URL = process.env.REACT_APP_BACKEND_URL;

var reminderTypes = ["maintenance", "inspection", "contract_renewal", "payment", "other"];
var frequencies = ["once", "weekly", "monthly", "quarterly", "yearly"];

var emptyForm = {
  title: "",
  reminder_type: "maintenance",
  property_id: "",
  unit_id: "",
  asset_id: "",
  supplier_id: "",
  description: "",
  frequency: "yearly",
  next_due_date: "",
  advance_notice_days: 14,
  is_active: true,
  notes: "",
};

export default function Reminders() {
  var { user, token } = useAuth();
  var { t } = useTranslation();
  var [reminders, setReminders] = useState([]);
  var [properties, setProperties] = useState([]);
  var [units, setUnits] = useState([]);
  var [assets, setAssets] = useState([]);
  var [suppliers, setSuppliers] = useState([]);
  var [loading, setLoading] = useState(true);
  var [dialogOpen, setDialogOpen] = useState(false);
  var [editingReminder, setEditingReminder] = useState(null);
  var [form, setForm] = useState(emptyForm);
  var [search, setSearch] = useState("");
  var [filterType, setFilterType] = useState("all");
  var [filterStatus, setFilterStatus] = useState("all");

  var canEdit = user?.role === "admin" || user?.role === "realestate_admin" || user?.role === "realestate_manager";
  var isAdmin = user?.role === "admin";

  useEffect(function() {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      var headers = token ? { Authorization: "Bearer " + token } : {};
      var opts = { credentials: "include", headers: headers };
      
      var remindersRes = await fetch(API_URL + "/api/reminders", opts);
      var propertiesRes = await fetch(API_URL + "/api/properties", opts);
      var assetsRes = await fetch(API_URL + "/api/assets", opts);
      var suppliersRes = await fetch(API_URL + "/api/suppliers", opts);
      
      if (remindersRes.ok) setReminders(await remindersRes.json());
      if (propertiesRes.ok) setProperties(await propertiesRes.json());
      if (assetsRes.ok) setAssets(await assetsRes.json());
      if (suppliersRes.ok) setSuppliers(await suppliersRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchUnits(propertyId) {
    try {
      var response = await fetch(API_URL + "/api/units?property_id=" + propertyId, {
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) setUnits(await response.json());
    } catch (error) {
      console.error("Error fetching units:", error);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      var url = editingReminder ? API_URL + "/api/reminders/" + editingReminder.reminder_id : API_URL + "/api/reminders";
      var method = editingReminder ? "PUT" : "POST";

      var payload = {
        title: form.title,
        reminder_type: form.reminder_type,
        property_id: form.property_id === "none" || !form.property_id ? null : form.property_id,
        unit_id: form.unit_id === "none" || !form.unit_id ? null : form.unit_id,
        asset_id: form.asset_id === "none" || !form.asset_id ? null : form.asset_id,
        supplier_id: form.supplier_id === "none" || !form.supplier_id ? null : form.supplier_id,
        description: form.description || null,
        frequency: form.frequency,
        next_due_date: form.next_due_date,
        advance_notice_days: parseInt(form.advance_notice_days) || 14,
        is_active: form.is_active,
        notes: form.notes || null,
      };

      var response = await fetch(url, {
        method: method,
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: "Bearer " + token } : {}) },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingReminder ? t("reminders.updated") : t("reminders.created"));
        setDialogOpen(false);
        setEditingReminder(null);
        setForm(emptyForm);
        fetchData();
      } else {
        var error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function handleEdit(reminder) {
    setEditingReminder(reminder);
    setForm({
      title: reminder.title || "",
      reminder_type: reminder.reminder_type || "maintenance",
      property_id: reminder.property_id || "",
      unit_id: reminder.unit_id || "",
      asset_id: reminder.asset_id || "",
      supplier_id: reminder.supplier_id || "",
      description: reminder.description || "",
      frequency: reminder.frequency || "yearly",
      next_due_date: reminder.next_due_date || "",
      advance_notice_days: reminder.advance_notice_days?.toString() || "14",
      is_active: reminder.is_active !== false,
      notes: reminder.notes || "",
    });
    if (reminder.property_id) {
      fetchUnits(reminder.property_id);
    }
    setDialogOpen(true);
  }

  async function handleComplete(reminderId) {
    try {
      var response = await fetch(API_URL + "/api/reminders/" + reminderId + "/complete", {
        method: "PUT",
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        toast.success(t("reminders.completed"));
        fetchData();
      } else {
        var error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  async function handleDelete(reminderId) {
    if (!window.confirm(t("common.confirmDelete"))) return;
    try {
      var response = await fetch(API_URL + "/api/reminders/" + reminderId, {
        method: "DELETE",
        credentials: "include",
        headers: token ? { Authorization: "Bearer " + token } : {},
      });
      if (response.ok) {
        toast.success(t("reminders.deleted"));
        fetchData();
      } else {
        var error = await response.json();
        toast.error(error.detail || t("common.error"));
      }
    } catch (error) {
      toast.error(t("common.error"));
    }
  }

  function getFilteredReminders() {
    var result = [];
    var searchLower = search.toLowerCase();
    for (var i = 0; i < reminders.length; i++) {
      var r = reminders[i];
      var matchSearch = r.title.toLowerCase().indexOf(searchLower) >= 0;
      var matchType = filterType === "all" || r.reminder_type === filterType;
      var matchStatus = filterStatus === "all" || 
        (filterStatus === "active" && r.is_active) || 
        (filterStatus === "inactive" && !r.is_active);
      if (matchSearch && matchType && matchStatus) result.push(r);
    }
    return result;
  }

  function getTypeBadge(type) {
    var colors = {
      maintenance: "bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400",
      inspection: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
      contract_renewal: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
      payment: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
      other: "bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400",
    };
    return <Badge className={colors[type] || colors.other}>{t("reminders.types." + type)}</Badge>;
  }

  function getDueBadge(dueDate, isActive) {
    if (!isActive) return <Badge variant="secondary">{t("reminders.inactive")}</Badge>;
    
    var today = new Date();
    var due = new Date(dueDate);
    var diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return <Badge variant="destructive"><AlertTriangle className="w-3 h-3 mr-1" />{t("reminders.overdue")}</Badge>;
    } else if (diffDays <= 7) {
      return <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">{t("reminders.dueSoon")}</Badge>;
    } else {
      return <Badge variant="outline">{t("reminders.upcoming")}</Badge>;
    }
  }

  function renderReminderTypeOptions() {
    var items = [];
    for (var i = 0; i < reminderTypes.length; i++) {
      items.push(<SelectItem key={reminderTypes[i]} value={reminderTypes[i]}>{t("reminders.types." + reminderTypes[i])}</SelectItem>);
    }
    return items;
  }

  function renderFrequencyOptions() {
    var items = [];
    for (var i = 0; i < frequencies.length; i++) {
      items.push(<SelectItem key={frequencies[i]} value={frequencies[i]}>{t("reminders.frequencies." + frequencies[i])}</SelectItem>);
    }
    return items;
  }

  function renderPropertyOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < properties.length; i++) {
      items.push(<SelectItem key={properties[i].property_id} value={properties[i].property_id}>{properties[i].name}</SelectItem>);
    }
    return items;
  }

  function renderUnitOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < units.length; i++) {
      items.push(<SelectItem key={units[i].unit_id} value={units[i].unit_id}>{units[i].name}</SelectItem>);
    }
    return items;
  }

  function renderAssetOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < assets.length; i++) {
      items.push(<SelectItem key={assets[i].asset_id} value={assets[i].asset_id}>{assets[i].name}</SelectItem>);
    }
    return items;
  }

  function renderSupplierOptions() {
    var items = [<SelectItem key="none" value="none">{t("common.none")}</SelectItem>];
    for (var i = 0; i < suppliers.length; i++) {
      if (suppliers[i].is_active !== false) {
        items.push(<SelectItem key={suppliers[i].supplier_id} value={suppliers[i].supplier_id}>{suppliers[i].name}</SelectItem>);
      }
    }
    return items;
  }

  function renderFilterTypeOptions() {
    var items = [<SelectItem key="all" value="all">{t("common.all")}</SelectItem>];
    for (var i = 0; i < reminderTypes.length; i++) {
      items.push(<SelectItem key={reminderTypes[i]} value={reminderTypes[i]}>{t("reminders.types." + reminderTypes[i])}</SelectItem>);
    }
    return items;
  }

  function renderReminderRows() {
    var filteredReminders = getFilteredReminders();
    var rows = [];
    for (var i = 0; i < filteredReminders.length; i++) {
      var reminder = filteredReminders[i];
      var reminderId = reminder.reminder_id;
      var reminderData = JSON.parse(JSON.stringify(reminder));
      rows.push(
        <TableRow key={reminderId} data-testid={"reminder-row-" + reminderId}>
          <TableCell className="font-medium">{reminder.title}</TableCell>
          <TableCell>{getTypeBadge(reminder.reminder_type)}</TableCell>
          <TableCell>{reminder.property_name || "-"}</TableCell>
          <TableCell>{reminder.next_due_date}</TableCell>
          <TableCell>{getDueBadge(reminder.next_due_date, reminder.is_active)}</TableCell>
          <TableCell>{t("reminders.frequencies." + reminder.frequency)}</TableCell>
          <TableCell>{reminder.last_completed_date || "-"}</TableCell>
          {isAdmin && (
            <TableCell>
              {reminder.owner_name ? (
                <div>
                  <div className="font-medium text-sm">{reminder.owner_name}</div>
                  <div className="text-xs text-muted-foreground">{reminder.owner_email}</div>
                </div>
              ) : "-"}
            </TableCell>
          )}
          {canEdit && (
            <TableCell>
              <div className="flex gap-1">
                {reminder.is_active && (
                  <Button variant="ghost" size="icon" onClick={handleComplete.bind(null, reminderId)} title={t("reminders.markComplete")}>
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </Button>
                )}
                <Button variant="ghost" size="icon" onClick={handleEdit.bind(null, reminderData)}><Pencil className="w-4 h-4" /></Button>
                <Button variant="ghost" size="icon" onClick={handleDelete.bind(null, reminderId)}><Trash2 className="w-4 h-4 text-destructive" /></Button>
              </div>
            </TableCell>
          )}
        </TableRow>
      );
    }
    return rows;
  }

  var filteredReminders = getFilteredReminders();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reminders-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("reminders.title")}</h1>
          <p className="text-muted-foreground mt-1">{t("reminders.subtitle")}</p>
        </div>
        {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={function(open) { setDialogOpen(open); if (!open) { setEditingReminder(null); setForm(emptyForm); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-reminder-btn"><Plus className="w-4 h-4 mr-2" />{t("reminders.addReminder")}</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingReminder ? t("reminders.editReminder") : t("reminders.addNewReminder")}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label className="form-label">{t("reminders.reminderTitle")} *</Label>
                    <Input value={form.title} onChange={function(e) { setForm({...form, title: e.target.value}); }} required placeholder={t("reminders.titlePlaceholder")} data-testid="reminder-title-input" />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("common.type")}</Label>
                    <Select value={form.reminder_type} onValueChange={function(v) { setForm({...form, reminder_type: v}); }}>
                      <SelectTrigger data-testid="reminder-type-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{renderReminderTypeOptions()}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">{t("reminders.frequency")}</Label>
                    <Select value={form.frequency} onValueChange={function(v) { setForm({...form, frequency: v}); }}>
                      <SelectTrigger data-testid="reminder-frequency-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{renderFrequencyOptions()}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("reminders.nextDueDate")} *</Label>
                    <Input type="date" value={form.next_due_date} onChange={function(e) { setForm({...form, next_due_date: e.target.value}); }} required data-testid="reminder-due-date-input" />
                  </div>
                  <div>
                    <Label className="form-label">{t("reminders.advanceNoticeDays")}</Label>
                    <Input type="number" value={form.advance_notice_days} onChange={function(e) { setForm({...form, advance_notice_days: e.target.value}); }} min="1" max="365" />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("assets.property")}</Label>
                    <Select value={form.property_id || "none"} onValueChange={function(v) { setForm({...form, property_id: v, unit_id: ""}); if (v && v !== "none") fetchUnits(v); else setUnits([]); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{renderPropertyOptions()}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">{t("common.unit")}</Label>
                    <Select value={form.unit_id || "none"} onValueChange={function(v) { setForm({...form, unit_id: v}); }} disabled={!form.property_id || form.property_id === "none"}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{renderUnitOptions()}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="form-label">{t("reminders.asset")}</Label>
                    <Select value={form.asset_id || "none"} onValueChange={function(v) { setForm({...form, asset_id: v}); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{renderAssetOptions()}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="form-label">{t("reminders.supplier")}</Label>
                    <Select value={form.supplier_id || "none"} onValueChange={function(v) { setForm({...form, supplier_id: v}); }}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>{renderSupplierOptions()}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="form-label">{t("common.description")}</Label>
                  <Textarea value={form.description} onChange={function(e) { setForm({...form, description: e.target.value}); }} rows={2} placeholder={t("reminders.descriptionPlaceholder")} />
                </div>

                <div>
                  <Label className="form-label">{t("common.notes")}</Label>
                  <Textarea value={form.notes} onChange={function(e) { setForm({...form, notes: e.target.value}); }} rows={2} />
                </div>

                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    id="is_active" 
                    checked={form.is_active} 
                    onChange={function(e) { setForm({...form, is_active: e.target.checked}); }}
                    className="w-4 h-4"
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">{t("reminders.isActive")}</Label>
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={function() { setDialogOpen(false); }}>{t("common.cancel")}</Button>
                  <Button type="submit" data-testid="reminder-submit-btn">{t("common.save")}</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder={t("reminders.searchPlaceholder")} value={search} onChange={function(e) { setSearch(e.target.value); }} className="pl-10" data-testid="reminder-search" />
        </div>
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder={t("common.type")} /></SelectTrigger>
          <SelectContent>{renderFilterTypeOptions()}</SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-full sm:w-32"><SelectValue placeholder={t("common.status")} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("common.all")}</SelectItem>
            <SelectItem value="active">{t("reminders.active")}</SelectItem>
            <SelectItem value="inactive">{t("reminders.inactive")}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {filteredReminders.length > 0 ? (
        <Card className="bento-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t("reminders.reminderTitle")}</TableHead>
                <TableHead>{t("common.type")}</TableHead>
                <TableHead>{t("assets.property")}</TableHead>
                <TableHead>{t("reminders.dueDate")}</TableHead>
                <TableHead>{t("common.status")}</TableHead>
                <TableHead>{t("reminders.frequency")}</TableHead>
                <TableHead>{t("reminders.lastCompleted")}</TableHead>
                {isAdmin && <TableHead>{t("common.owner")}</TableHead>}
                {canEdit && <TableHead className="w-28">{t("common.actions")}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>{renderReminderRows()}</TableBody>
          </Table>
        </Card>
      ) : (
        <Card className="bento-card">
          <CardContent className="py-12 text-center text-muted-foreground">
            <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>{t("reminders.noReminders")}</p>
            {canEdit && <Button className="mt-4" onClick={function() { setDialogOpen(true); }}>{t("reminders.addFirstReminder")}</Button>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
